import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
} from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isAtNodeEnd } from '@lexical/selection';
import { mergeRegister } from '@lexical/utils';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  Code,
  Link,
  Subscript,
  Superscript,
  FormatClear,
  TextFormat,
} from '@mui/icons-material';
import classes from './FloatingTextFormatToolbarPlugin.module.scss';

function getSelectedNode(selection: any) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) return anchorNode;
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

function positionFloatingToolbar(
  toolbar: HTMLElement,
  nativeSelection: Selection,
  anchorElem: HTMLElement,
) {
  const domRange = nativeSelection.rangeCount > 0 ? nativeSelection.getRangeAt(0) : null;
  if (!domRange) return;

  const rect = domRange.getBoundingClientRect();
  const anchorRect = anchorElem.getBoundingClientRect();

  let top = rect.top - anchorRect.top - toolbar.offsetHeight - 8;
  let left = rect.left - anchorRect.left + rect.width / 2 - toolbar.offsetWidth / 2;

  if (left < 0) left = 0;
  if (left + toolbar.offsetWidth > anchorRect.width) left = anchorRect.width - toolbar.offsetWidth;
  if (top < 0) top = rect.bottom - anchorRect.top + 8;

  toolbar.style.top = `${top}px`;
  toolbar.style.left = `${left}px`;
}

/** Apply a text-content transformation to all selected text nodes */
function applyTextTransform(editor: LexicalEditor, transform: (text: string) => string) {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      selection.getNodes().forEach((node) => {
        if ($isTextNode(node)) {
          node.setTextContent(transform(node.getTextContent()));
        }
      });
    }
  });
}

const toUpperCase = (s: string) => s.toUpperCase();
const toLowerCase = (s: string) => s.toLowerCase();
const toCapitalize = (s: string) => s.replace(/\b\w/g, (c) => c.toUpperCase());

// ── Aa dropdown ──────────────────────────────────────────────────────────────

interface AaDropdownProps {
  editor: LexicalEditor;
  isSubscript: boolean;
  isSuperscript: boolean;
}

function AaDropdown({ editor, isSubscript, isSuperscript }: AaDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Position menu above the button (same as the toolbar), clamped to viewport
  useEffect(() => {
    if (!isOpen || !menuRef.current || !btnRef.current) return;
    const btnRect = btnRef.current.getBoundingClientRect();
    const menuEl = menuRef.current;
    const menuH = menuEl.offsetHeight;
    // Try to show menu above the button; fall back to below
    const top = btnRect.top - menuH - 4 > 0 ? btnRect.top - menuH - 4 : btnRect.bottom + 4;
    const left = Math.max(4, Math.min(btnRect.left, window.innerWidth - menuEl.offsetWidth - 4));
    menuEl.style.top = `${top}px`;
    menuEl.style.left = `${left}px`;
  }, [isOpen]);

  const clearFormatting = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.getNodes().forEach((node) => {
          if ($isTextNode(node)) node.setFormat(0);
        });
      }
    });
    setIsOpen(false);
  };

  const item = (active: boolean, onClick: () => void, icon: React.ReactNode, label: string) => (
    <button
      className={`${classes.aaItem} ${active ? classes.aaItemActive : ''}`}
      onMouseDown={(e) => {
        e.preventDefault(); // keep selection alive
        onClick();
      }}
    >
      <span className={classes.aaItemIcon}>{icon}</span>
      <span>{label}</span>
      {active && <span className={classes.aaCheck}>✓</span>}
    </button>
  );

  return (
    <div ref={dropdownRef} className={classes.aaDropdownWrapper}>
      <button
        ref={btnRef}
        className={`${classes.btn} ${isSubscript || isSuperscript ? classes.active : ''}`}
        title="Text Formatting"
        onMouseDown={(e) => {
          e.preventDefault();
          setIsOpen((v) => !v);
        }}
      >
        <TextFormat fontSize="small" />
      </button>
      {isOpen && (
        <div ref={menuRef} className={classes.aaMenu}>
          {item(
            false,
            () => {
              applyTextTransform(editor, toUpperCase);
              setIsOpen(false);
            },
            <span className={classes.caseIcon}>ABC</span>,
            'Uppercase',
          )}
          {item(
            false,
            () => {
              applyTextTransform(editor, toLowerCase);
              setIsOpen(false);
            },
            <span className={classes.caseIcon}>abc</span>,
            'Lowercase',
          )}
          {item(
            false,
            () => {
              applyTextTransform(editor, toCapitalize);
              setIsOpen(false);
            },
            <span className={classes.caseIcon}>Tt</span>,
            'Capitalize',
          )}
          <div className={classes.aaDivider} />
          {item(
            isSubscript,
            () => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
              setIsOpen(false);
            },
            <Subscript fontSize="small" />,
            'Subscript',
          )}
          {item(
            isSuperscript,
            () => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
              setIsOpen(false);
            },
            <Superscript fontSize="small" />,
            'Superscript',
          )}
          <div className={classes.aaDivider} />
          {item(false, clearFormatting, <FormatClear fontSize="small" />, 'Clear Formatting')}
        </div>
      )}
    </div>
  );
}

// ── Main floating toolbar ─────────────────────────────────────────────────────

interface FloatingToolbarProps {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isStrikethrough: boolean;
  isCode: boolean;
  isLink: boolean;
  isSubscript: boolean;
  isSuperscript: boolean;
}

function FloatingToolbar({
  editor,
  anchorElem,
  isBold,
  isItalic,
  isUnderline,
  isStrikethrough,
  isCode,
  isLink,
  isSubscript,
  isSuperscript,
}: FloatingToolbarProps) {
  const toolbarRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const toolbar = toolbarRef.current;
    const nativeSelection = window.getSelection();
    if (!toolbar || !nativeSelection || nativeSelection.rangeCount === 0) return;
    positionFloatingToolbar(toolbar, nativeSelection, anchorElem);
  }, [anchorElem]);

  useEffect(() => {
    updatePosition();
  }, [
    isBold,
    isItalic,
    isUnderline,
    isStrikethrough,
    isCode,
    isLink,
    isSubscript,
    isSuperscript,
    updatePosition,
  ]);

  // Keep the toolbar anchored to the selection while the doc scrolls or the
  // window resizes (the toolbar is only mounted while a selection is active, so
  // these listeners are scoped to that window and torn down on unmount). The
  // editor's scroll container is a descendant of anchorElem and scroll events
  // don't bubble, so listen on window with capture=true to catch scrolls from
  // any container. Purely repositions — no editor state is touched.
  useEffect(() => {
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  const insertLink = useCallback(() => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, isLink ? null : 'https://');
  }, [editor, isLink]);

  const btn = (active: boolean, onClick: () => void, title: string, icon: React.ReactNode) => (
    <button
      onMouseDown={(e) => {
        e.preventDefault(); // preserve selection
        onClick();
      }}
      className={`${classes.btn} ${active ? classes.active : ''}`}
      title={title}
    >
      {icon}
    </button>
  );

  return (
    <div ref={toolbarRef} className={classes.floatingToolbar}>
      {btn(
        isBold,
        () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold'),
        'Bold',
        <FormatBold fontSize="small" />,
      )}
      {btn(
        isItalic,
        () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic'),
        'Italic',
        <FormatItalic fontSize="small" />,
      )}
      {btn(
        isUnderline,
        () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline'),
        'Underline',
        <FormatUnderlined fontSize="small" />,
      )}
      {btn(
        isStrikethrough,
        () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough'),
        'Strikethrough',
        <StrikethroughS fontSize="small" />,
      )}
      <span className={classes.separator} />
      {btn(
        isCode,
        () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code'),
        'Inline Code',
        <Code fontSize="small" />,
      )}
      {btn(isLink, insertLink, 'Link', <Link fontSize="small" />)}
      <span className={classes.separator} />
      <AaDropdown editor={editor} isSubscript={isSubscript} isSuperscript={isSuperscript} />
    </div>
  );
}

// ── Plugin ────────────────────────────────────────────────────────────────────

export function FloatingTextFormatToolbarPlugin({ anchorElem }: { anchorElem: HTMLElement }) {
  const [editor] = useLexicalComposerContext();
  const [isVisible, setIsVisible] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);

  const updateFloatingToolbar = useCallback(() => {
    try {
      editor.read(() => {
        if (editor.isComposing()) return;

        const selection = $getSelection();
        const nativeSelection = window.getSelection();
        const rootElement = editor.getRootElement();

        if (
          selection !== null &&
          $isRangeSelection(selection) &&
          !selection.isCollapsed() &&
          rootElement !== null &&
          rootElement.contains(nativeSelection?.anchorNode ?? null)
        ) {
          setIsBold(selection.hasFormat('bold'));
          setIsItalic(selection.hasFormat('italic'));
          setIsUnderline(selection.hasFormat('underline'));
          setIsStrikethrough(selection.hasFormat('strikethrough'));
          setIsCode(selection.hasFormat('code'));
          setIsSubscript(selection.hasFormat('subscript'));
          setIsSuperscript(selection.hasFormat('superscript'));

          const node = getSelectedNode(selection);
          const parent = node.getParent();
          setIsLink($isLinkNode(parent) || $isLinkNode(node));

          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      });
    } catch {
      setIsVisible(false);
    }
  }, [editor]);

  useEffect(() => {
    const handler = () => {
      try {
        updateFloatingToolbar();
      } catch {
        // Editor may not be active (e.g. selection changed outside editor)
      }
    };
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, [updateFloatingToolbar]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updateFloatingToolbar();
      }),
    );
  }, [editor, updateFloatingToolbar]);

  if (!isVisible) return null;

  return createPortal(
    <FloatingToolbar
      editor={editor}
      anchorElem={anchorElem}
      isBold={isBold}
      isItalic={isItalic}
      isUnderline={isUnderline}
      isStrikethrough={isStrikethrough}
      isCode={isCode}
      isLink={isLink}
      isSubscript={isSubscript}
      isSuperscript={isSuperscript}
    />,
    anchorElem,
  );
}
